<template>
  <div class='bg-pink-100 min-h-screen flex items-center justify-center'>
    <div v-if='!isAuthorized'>
      <moderator-logout />
    </div>
    <div v-else class='w-full h-full flex flex-row'>
      <div class='px-11 pt-48 bg-pink-50 h-screen flex flex-1 flex-col'>
        <div class='flex justify-center items-center text-center text-3xl font-semibold'>
          {{ questionNumberTitle }}
        </div>
        <div class='flex flex-row justify-center items-center text-center text-3xl font-semibold pb-4'>
          {{ questionText }}
        </div>
        <div v-if='isVotingFinished' class='pt-16 flex flex-row justify-center items-center text-2xl font-semibold text-pink-500'>
          Voting Has Finished
        </div>
        <div v-else class='pt-16 flex flex-row justify-center items-center text-2xl font-semibold text-pink-500'>
          Still Accepting Answers...
        </div>
        <moderator-results-chart
          v-if='hasResponses'
          :key='sessionVotingResultsKey'
          :session-voting-question-id='sessionVotingId' />
        <div v-else class='pt-16 flex flex-row justify-center items-center text-xl text-gray-400'>
          No Responses Yet
        </div>
        <div v-if='sessionHasMultipleVotings'
          class='flex flex-row justify-center items-center h-screen pb-11 gap-x-11'>
          <button
            class='logout-button w-full lg:w-64 uppercase border-2 border-pink-500 text-pink-500 text-center font-semibold rounded-md px-11 py-4 hover:shadow-md cursor-pointer'
            @click='goToPreviousVoting'>
            Previous Question
          </button>
          <button
            class='logout-button w-full lg:w-64 uppercase border-2 border-pink-500 text-pink-500 text-center font-semibold rounded-md px-11 py-4 hover:shadow-md cursor-pointer'
            @click='goToNextVoting'>
            Next Question
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ModeratorResultsChart from '@/views/gbcc2025/ModeratorResultsChart.vue'
import DynamicLinkHelper from '@/utils/dynamic-link-helper'
import ModeratorLogout from '@/views/gbcc2025/ModeratorLogout.vue'
import { mapState, mapGetters, mapActions }  from 'vuex'
import { mapFields } from 'vuex-map-fields'

export default {
  name: 'ModeratorResults',
  components: {
    ModeratorResultsChart,
    ModeratorLogout,
  },
  data () {
    return {
      dynamicLink: '',
    }
  },
  watch: {
    selectedSession: {
      handler(newSession) {
        if (newSession && newSession.listOfSessionVotings?.length > 0) {
          this.generateDynamicLink()
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('users', [
      'isAdmin',
      'isModerator',
    ]),
    ...mapState('sessionVoting', [
      'sessionVotingQuestions',
    ]),
    ...mapGetters('events', [
      'eventConfigLogoImageUrl',
    ]),
    ...mapFields('sessionVoting', [
      'selectedVotingQuestion',
    ]),
    selectedSessionId () {
      return parseInt(this.$route.query.session_id)
    },
    sessionVotingId () {
      return parseInt(this.$route.query.session_voting_id)
    },
    selectedSession () {
      for (const question of this.sessionVotingQuestions) {
        const session = question.listOfSessions.find(session => session.id === this.selectedSessionId)
        if (session) {
          session.listOfSessionVotings = session.listOfSessionVotings?.sort((a, b) => a.id - b.id) || []
          return session
        }
      }
      return {}
    },
    logoUrl () {
      return  this.eventConfigLogoImageUrl
    },
    votingsCounter () {
      return this.selectedSession?.listOfSessionVotings?.length ?? 0
    },
    sessionHasMultipleVotings () {
      return this.votingsCounter > 1
    },
    numberOfCurrentVoting () {
      if (!this.selectedSession) {
        return 0
      }
      return this.selectedSession.listOfSessionVotings?.findIndex(voting => voting.id === this.sessionVotingId) + 1
    },
    questionText () {
      return this.selectedVotingQuestion ? this.selectedVotingQuestion.question : ''
    },
    sessionVotingChoices () {
      return this.selectedVotingQuestion ? this.selectedVotingQuestion.choices : []
    },
    totalResponses () {
      return this.selectedVotingQuestion?.sessionVotingResults.reduce(
        (total, result) => total + result.choicedCount, 0)
    },
    isVotingFinished () {
      return this.selectedVotingQuestion?.status.toLowerCase() === 'finished'
    },
    numberOfResponses () {
      const total = this.totalResponses ?? 0
      return total === 1 ? '1 response' : `${total} responses`
    },
    hasResponses () {
      return this.totalResponses > 0
    },
    questionNumberTitle () {
      return `Q${this.numberOfCurrentVoting}`
    },
    isAuthorized () {
      return this.isAdmin || this.isModerator
    },
    sessionVotingResultsKey () {
      const results = this.selectedVotingQuestion?.sessionVotingResults || []
      const totalResponses = results.length

      if (totalResponses === 1) {
        return `single-${results[0]?.choiceId || 'none'}`
      }

      const checksum = results.reduce((acc, result) => acc + (result.choiceId || 0) + (result.choicedCount || 0), 0)
      return `total-${totalResponses}-${checksum}`
    },
  },
  methods: {
    ...mapActions('sessionVoting', [
      'getSessionVotingQuestions',
      'getSessionVotingQuestionData',
    ]),
    goToPreviousVoting () {
      if (!this.selectedSession) {
        return
      }
      let index = this.selectedSession.listOfSessionVotings?.findIndex(voting => voting.id === this.sessionVotingId)
      if (index > 0) {
        let voting = this.selectedSession.listOfSessionVotings[index - 1]
        this.$router.push({ name: 'ModeratorResults', query: {...this.$route.query, session_voting_id: voting.id, session_id: this.selectedSessionId } })
        this.updateQuestionData()
      }
    },
    goToNextVoting () {
      if (!this.selectedSession) {
        return
      }
      let index = this.selectedSession.listOfSessionVotings?.findIndex(voting => voting.id === this.sessionVotingId)
      if (index < this.selectedSession.listOfSessionVotings?.length - 1) {
        let voting = this.selectedSession.listOfSessionVotings[index + 1]
        this.$router.push({ name: 'ModeratorResults', query: {...this.$route.query, session_voting_id: voting.id, session_id: this.selectedSessionId } })
        this.updateQuestionData()
      }
    },
    updateQuestionData() {
      if (this.sessionVotingId) {
        this.selectedQuestionId = this.sessionVotingId
        this.getSessionVotingQuestionData(this.selectedQuestionId)
      }
    },
    goToModeratorHome () {
      this.$router.push({ name: 'Moderator' })
    },
    async generateDynamicLink () {
      if (!this.selectedSession || !this.selectedSession.listOfSessionVotings || this.selectedSession.listOfSessionVotings.length === 0) {
        return
      }
      const votingQuestion = this.selectedSession?.listOfSessionVotings[0]
      const link = await DynamicLinkHelper.getVotingDynamicLink(
          this.selectedSessionId,
          votingQuestion.id
        )
      this.dynamicLink = link
    },
  },
  mounted () {
    this.$emit('update:layout', 'empty-page')
    this.getSessionVotingQuestions()
    this.updateQuestionData()

    this.updateInterval = setInterval(() => {
      this.getSessionVotingQuestions()
      this.updateQuestionData()
    }, 5000)
  },
  beforeDestroy () {
    this.$emit('update:layout', 'default-layout')
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
}
</script>
<style scoped>
.custom-leading {
  line-height: 1.5;
}
</style>